import React from 'react'
import { Stack, Tab, Tabs } from 'react-bootstrap'
import { useKey } from 'react-use'
import type { Chat, LinkChatRequesterArgs } from '@ztechapps/common.contract'
import type { ContactSelectProps, CustomerInfoFormProps, VendorInfoFormProps } from '@ztechapps/webui.contract'
import { UserTypeEnum } from '@ztechapps/common.contract'
import { useActionMutation, useError, useLazyDocument } from '@ztechapps/common.hook'
import { toCustomerSelectValue } from '@ztechapps/customer.common'
import { Field, Form, FormSaveCancelButtons } from '@ztechapps/webui.form'
import { HorizontalStack, TooltipPopOver } from '@ztechapps/webui.util'
import { useInjectComponent, WebuiIocKey } from '@ztechapps/webui.contract'
import { useConversationHost } from '../ConversationHost'
import { conversationDetailsQuery, linkChatRequesterMutation } from '../Inbox/graphql'

// A component of its own so the info form is looked up only for a host that offers one.
const CustomerInfoEditor = (props: CustomerInfoFormProps) => {
  const CustomerInfoForm = useInjectComponent<CustomerInfoFormProps>(WebuiIocKey.customer.customerInfoForm)

  return <CustomerInfoForm {...props} />
}

// Likewise for the vendor form: only a linked vendor looks it up.
const VendorInfoEditor = (props: VendorInfoFormProps) => {
  const VendorInfoForm = useInjectComponent<VendorInfoFormProps>(WebuiIocKey.vendor.vendorInfoForm)

  return <VendorInfoForm {...props} />
}

export type ConversationCustomerPopoverProps = {
  chat?: Chat
  linked: boolean
  show: boolean
  onClose: () => void
  /** The link that opens the popover — its anchor, so the popover opens right beside it. */
  children: React.ReactElement
}

export const ConversationCustomerPopover = ({
  chat,
  linked,
  show,
  onClose,
  children,
}: ConversationCustomerPopoverProps) => {
  const { hasCustomerInfoForm = true } = useConversationHost()
  const ContactSelect = useInjectComponent<ContactSelectProps>(WebuiIocKey.customer.contactSelect)
  const [onExecute] = useActionMutation<Chat, LinkChatRequesterArgs>({ query: linkChatRequesterMutation })
  // Keyed like the panel's own read (ConversationDetailsPanel.tsx), so the fresh result lands in the cache entry the
  // panel watches.
  const { error, executeQuery } = useLazyDocument<Chat>({
    query: conversationDetailsQuery,
    filter: { externalId: chat?.externalId },
    options: { queryOptions: { fetchPolicy: 'network-only' } },
  })
  useError(error)

  // Capture phase, so `aria-expanded` is read before react-select closes an open menu on this same Escape.
  useKey(
    'Escape',
    (event) => {
      if (show && !(event.target instanceof Element && event.target.getAttribute('aria-expanded') === 'true')) onClose()
    },
    { options: true },
    [show]
  )

  const requester = chat?.requester
  const initialValues = { requester: requester ? toCustomerSelectValue(requester) : null }
  const validate = (values: typeof initialValues) => (values.requester?._id ? {} : { requester: 'Contact is required' })
  const onSubmit = async (values: typeof initialValues) => {
    await onExecute({ chatId: chat?._id, requesterId: values.requester._id })
  }

  // `updateCustomer` returns a Customer, a different cache entity from the panel's User requester, so the saved phone
  // and company reach the widget only through a fresh read of the conversation. This hook is a separate instance from
  // the panel's own read, so executing it never flips the panel's loading state — which would make DataSuspense
  // replace the whole rail, this popover included.
  const onCustomerSaved = async () => {
    await executeQuery()
    onClose()
  }

  // The same two tabs as the customer editor on the sales and logistics screens
  // (apps/panelpunches/admin/src/components/change-or-edit-customer/index.tsx:24-37).
  const showEditTab = linked && hasCustomerInfoForm
  const tooltipBody = (
    <Tabs variant="underline" defaultActiveKey={showEditTab ? 'edit' : 'change'}>
      {/* A ternary, not ShowIf: Tabs turns each element child into a pane, and a wrapper is not a Tab. */}
      {showEditTab ? (
        <Tab title="Edit Info" eventKey="edit" className="pt-3 pb-2">
          {requester?.type === UserTypeEnum.vendor ? (
            <VendorInfoEditor vendor={requester} onSaved={onCustomerSaved} onCancel={onClose} />
          ) : (
            <CustomerInfoEditor customer={requester} onSaved={onCustomerSaved} onCancel={onClose} />
          )}
        </Tab>
      ) : null}
      <Tab title="Choose other 'Contact'" eventKey="change" className="py-2">
        <Form
          initialValues={initialValues}
          validate={validate}
          onSubmit={onSubmit}
          afterSubmit={onClose}
          afterReset={onClose}
        >
          <Stack gap={2}>
            <Field name="requester" label="Contact" component={ContactSelect} onChange />
            <HorizontalStack className="justify-content-end">
              <div>
                <FormSaveCancelButtons />
              </div>
            </HorizontalStack>
          </Stack>
        </Form>
      </Tab>
    </Tabs>
  )

  return (
    <TooltipPopOver show={show} showHeader={false} width={480} placement="right-start" flip tooltipBody={tooltipBody}>
      {children}
    </TooltipPopOver>
  )
}
